/* global google */

/** Matches the iOS TrackPoint table exactly. */
export type TrackPoint = {
  id: number
  latitude: number
  longitude: number
  altitude: number | null
  horizontalAccuracy: number | null
  speed: number | null
  course: number | null
  timestamp: string // ISO / "YYYY-MM-DD HH:MM:SS.sss"
  source: number
  segmentIndex: number
}

/** Matches the iOS VisitRecords table. */
export type VisitRecord = {
  id: number
  arrivalDate: string
  /** Null means the visit never got a CLVisit departure (ongoing). */
  departureDate: string | null
  latitude: number
  longitude: number
  horizontalAccuracy: number
  invalid: number
}

export type GoogleType = typeof google

/** TrackPointSource enum from DeerGo iOS (see TrackPoint.swift). */
export const SOURCE_LIVE_UPDATES = 1
export const SOURCE_FALLBACK_LOCATION = 2
export const SOURCE_SLC = 3
export const SOURCE_GPS_STOP = 12
export const SOURCE_GPX_IMPORT = 100
export const SOURCE_KML_IMPORT = 101

export const SOURCE_NAMES: Record<number, string> = {
  [SOURCE_LIVE_UPDATES]: 'liveUpdates',
  [SOURCE_FALLBACK_LOCATION]: 'startUpdatingLocation',
  [SOURCE_SLC]: 'slc',
  [SOURCE_GPS_STOP]: 'gpsStop',
  [SOURCE_GPX_IMPORT]: 'gpxImport',
  [SOURCE_KML_IMPORT]: 'kmlImport',
}

/** Color for each TrackPoint source (RGB, 0-255). Alpha added per-layer. */
export const SOURCE_RGB: Record<number, [number, number, number]> = {
  [SOURCE_LIVE_UPDATES]: [52, 199, 89], // green
  [SOURCE_FALLBACK_LOCATION]: [255, 149, 0], // orange
  [SOURCE_SLC]: [175, 82, 222], // purple
  [SOURCE_GPS_STOP]: [255, 59, 48], // red
  [SOURCE_GPX_IMPORT]: [0, 122, 255], // blue
  [SOURCE_KML_IMPORT]: [90, 200, 250], // cyan
}

/** RGB→"#rrggbb" for CSS/legend use. */
export function rgbToHex([r, g, b]: [number, number, number]): string {
  const h = (n: number) => n.toString(16).padStart(2, '0')
  return '#' + h(r) + h(g) + h(b)
}

export type SourceFilters = {
  [SOURCE_LIVE_UPDATES]: boolean
  [SOURCE_FALLBACK_LOCATION]: boolean
  [SOURCE_SLC]: boolean
  visits: boolean
}

export const DEFAULT_SOURCE_FILTERS: SourceFilters = {
  [SOURCE_LIVE_UPDATES]: true,
  [SOURCE_FALLBACK_LOCATION]: true,
  [SOURCE_SLC]: true,
  visits: true,
}

/** 48h max window (in hours) to keep point counts manageable. */
export const MAX_WINDOW_HOURS = 48
/** Default window shown on first load. */
export const DEFAULT_WINDOW_HOURS = 24
/** Window slider step (minutes). */
export const WINDOW_STEP_MINUTES = 10
